/**
 * Customer-facing query for the tenant's enabled delivery countries.
 *
 * Backed by ``GET /checkout/delivery-countries`` on the API; the
 * admin counterpart at ``/admin/delivery-countries`` toggles the
 * underlying whitelist. The two hooks share the same data source
 * but the customer endpoint omits master-list / disabled rows so
 * the FE can drive both the country ``<select>`` and the
 * phone-prefix ``<select>`` from one fetch.
 *
 * Cache horizon is intentionally generous (5 min) — the list rarely
 * changes mid-session. The admin mutation explicitly invalidates
 * this key after a save, so an operator's toggle still propagates
 * to the customer view immediately on the next interaction.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

const API = "/api/v1";

export interface CheckoutCountry {
  code: string;
  name_cs: string;
  name_en: string;
  /** `{jazyk: název}` z platformního katalogu zemí (KAN-1449). */
  name_i18n: Record<string, string>;
  dial_code: string;
  flag_emoji: string;
  /** Hledací klíče comboboxu (KAN-1454). */
  alpha3: string | null;
  aliases: string[];
  /** Nápovědy adresního formuláře (KAN-1456); `null` = bez kontroly. */
  postal_code_regex: string | null;
  /** PSČ, které vzoru vyhoví — pro hlášku „špatný formát, např. …". */
  postal_code_example: string | null;
  address_format: string | null;
  /**
   * KAN-1657 — má země aktivního dopravce? Země bez něj se jako doručovací
   * nenabídne; obchod dál prodává do ostatních zemí.
   */
  shippable: boolean;
}

export function useCheckoutDeliveryCountries() {
  return useQuery<CheckoutCountry[]>({
    queryKey: ["checkout", "deliveryCountries"],
    queryFn: () =>
      apiFetch<CheckoutCountry[]>(`${API}/checkout/delivery-countries`),
    staleTime: 5 * 60_000,
  });
}
